import {useFluxStore} from 'client/framework';
import classnames from 'classnames';
import type {List} from 'immutable';
import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTracking} from 'react-tracking';

import {
  addToast,
  Anchor,
  Banner,
  Button,
  DropdownItem,
  Heading,
  Icon,
  MenuListItem,
  MobileDropdown,
  NoDataMsg,
  Text,
  WithTooltip
} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {ClassroomModelV1, SubjectModelV2, TeacherModelV1} from '@albert-io/models';
import appStore from 'client/AppStore';
import LoadingIndicator from 'generic/LoadingIndicator.react';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {useUpdateClassroomSubjectAccessMutation} from 'gql/shared/ClassroomSubjectAccess.queries.generated';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';

import EmptyState from '../EmptyState/index.react';

import RestrictFreePracticeModal from './RestrictFreePracticeModal.react';
import {SubjectsListCard} from './SubjectsListCard.react';
import './teacher-subjects-list.scss';

const TrackedEmptyState = ({title, body}: {title: string; body: string}) => {
  const {Track} = useTracking({feature: 'Subjects'});
  return (
    <Track>
      <EmptyState title={title} body={body} />
    </Track>
  );
};

const getClassroomQuery = (classId: string) => {
  return resource('classroom_v1')
    .mandarkEndpoint(['classrooms_v1', classId])
    .include('subjects_v2')
    .include('subjects_v2.curriculum_area_v1')
    .include('subjects_v2.guides_v1')
    .withMeta('subject_v2,classroom_v1')
    .customQuery({
      meta: {
        context: {
          classroom: {
            licensed_subjects: true
          }
        }
      }
    });
};

export const getTeacherQuery = (teacherId = masqueradeStore.getUserIdByMasqueradeState()) => {
  return resource('teacher_v1')
    .mandarkEndpoint(['teachers_v1', teacherId])
    .include('school_v5')
    .withMeta('teacher_v1')
    .customQuery({
      meta: {
        context: {
          teacher: {
            license_info: true
          }
        }
      }
    });
};

const EmptySubjectsList = () => (
  <NoDataMsg.Container centered className='teacher-subjects-list--empty'>
    <NoDataMsg.Body>
      Students can only practice on questions you&apos;ve assigned to them. If you want to allow
      students to answer any question in a subject,&nbsp;
      <Anchor to='/subjects/high-school/test-prep'>visit the Library tab</Anchor>
      &nbsp;and toggle on access.
    </NoDataMsg.Body>
  </NoDataMsg.Container>
);

interface SubjectsListProps {
  classroom: ClassroomModelV1;
  handleCloseAccess: (subject: SubjectModelV2) => void;
  handleOpenAccess: (subject: SubjectModelV2) => void;
  handlePartialCreditToggled: (classroomId: string, subject: SubjectModelV2) => void;
  selectedMenuItem: string;
  subjects: List<SubjectModelV2>;
  teacher: TeacherModelV1;
}

const SubjectsList = ({
  classroom,
  handleCloseAccess,
  handleOpenAccess,
  handlePartialCreditToggled,
  selectedMenuItem,
  subjects,
  teacher
}: SubjectsListProps) => {
  const [showHidden, setShowHidden] = useState<boolean>(false);
  const schoolId = teacher.hasSchool() ? teacher.getSchool().getId() : null;
  const isSchoolLicensed = schoolId != null && Boolean(teacher.getLicenseStatusForSchool(schoolId));
  const isTeacherLicensed = teacher.getMeta().isLicensed();

  const hiddenSubjects = useMemo(
    () => subjects.filter((subject) => subject.isHidden()),
    [subjects]
  );

  const sortedSubjects = useMemo(
    () => subjects.sortBy((subject) => subject.getName().toLowerCase()),
    [subjects]
  );

  const headingTooltipText =
    selectedMenuItem === 'Independent practice' ? (
      <p className='mb-x0!'>
        If a subject has open access, that means students can independently practice and view
        solutions for any question in that subject&apos;s Practice tab. Otherwise, students can only
        practice and view solutions from questions that you&apos;ve assigned to them.
        <br />
        <br />
        Close access by selecting a subject below. Open access to more subjects from our Subjects
        library.
      </p>
    ) : (
      <p className='mb-x0!'>Subjects practiced by your students.</p>
    );

  const hiddenSubjectsTooltipText = (
    <p className='mb-x0!'>
      Hidden subjects are no longer publicly available. Their guides are not accessible to teachers
      or students. However, assignments for these subjects will continue to work and data from these
      subjects will continue to appear in reports.
    </p>
  );

  return (
    <>
      <div className='teacher-subjects-list__subjects-container'>
        <Heading
          size='m'
          className='mb-x3 flex items-center justify-between'
          style={{
            gridColumn: '1/-1'
          }}
        >
          <div>
            {selectedMenuItem === 'Independent practice'
              ? 'Independent practice'
              : 'All class subjects'}
            <WithTooltip content={headingTooltipText} placement='right' theme='light'>
              <Icon
                icon='question-circle'
                color='tertiary'
                size='standalone'
                className='ml-x2'
                iconStyle='regular'
              />
            </WithTooltip>
          </div>
          {hiddenSubjects.size > 0 && (
            <div>
              <Button
                variant={!showHidden ? 'text' : 'faded'}
                roundness='pill'
                size='s'
                color={!showHidden ? 'secondary' : 'primary'}
                onClick={() => setShowHidden(!showHidden)}
                className='gap-x1 font-normal!'
              >
                Show Hidden subjects
                <WithTooltip content={hiddenSubjectsTooltipText} placement='right' theme='light'>
                  <Icon icon='info-circle' iconStyle='regular' />
                </WithTooltip>
                {showHidden && <Icon icon='close' className='ml-x1' />}
              </Button>
            </div>
          )}
        </Heading>
        {selectedMenuItem === 'Independent practice' && (
          <Banner className='mb-x4'>
            <Banner.Icon icon='info' iconStyle='solid' />
            <span>
              Open access to more subjects from the&nbsp;
              <Anchor to='/subjects/high-school/test-prep'>Library</Anchor>
            </span>
          </Banner>
        )}
        <div className='teacher-subjects-list__subject-cards'>
          {sortedSubjects
            .map((subject) => {
              const isHidden = subject.isHidden();
              const shouldShowSubjectActions =
                subject.isAdaptive() || (isTeacherLicensed && isSchoolLicensed);
              if (!showHidden && isHidden) {
                return null;
              }
              return (
                <SubjectsListCard
                  classroom={classroom}
                  isHidden={isHidden}
                  isAdaptive={subject.isAdaptive()}
                  showActions={shouldShowSubjectActions}
                  subject={subject}
                  handleCloseAccess={handleCloseAccess}
                  handleOpenAccess={handleOpenAccess}
                  handlePartialCreditToggled={handlePartialCreditToggled}
                  key={subject.getId()}
                />
              );
            })
            .toArray()}
        </div>
      </div>
    </>
  );
};

interface TeacherSubjectsListProps {
  params: {
    classId: string;
  };
}

const TeacherSubjectsList = ({params}: TeacherSubjectsListProps) => {
  const isMobile = useFluxStore(appStore, (s) => s.isMobileView());
  const {classId} = params;

  const classroomQuery = useMemo(() => getClassroomQuery(classId), [classId]);

  useEffect(() => {
    // Invalidate when classId changes to ensure fresh data
    classroomQuery.invalidateInterest();
  }, [classId, classroomQuery]);

  const {
    isReady,
    resources: [teacher, classroom]
  } = useMandarkQueries([getTeacherQuery(), classroomQuery]);

  const [selectedMenuItem, setSelectedMenuItem] = useState<string>('All class subjects');
  const [selectedSubject, setSelectedSubject] = useState<SubjectModelV2 | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [updateClassroomSubjectAccess] = useUpdateClassroomSubjectAccessMutation();

  const findUnrestrictedSubjects = (classroomId: string): List<SubjectModelV2> =>
    classroom
      .getSubjects()
      .filter(
        (subject) => !subject.getClassroomRelationships(classroomId).getRestrictFreePractice()
      );

  const handleCloseAccess = useCallback(
    (subject: SubjectModelV2) => {
      setSelectedSubject(subject);
      setShowModal(true);
    },
    [setSelectedSubject, setShowModal]
  );

  const handleSubjectAccessChange = useCallback(
    async (classroomArg: ClassroomModelV1, subject: SubjectModelV2, restricted: boolean) => {
      try {
        const result = await updateClassroomSubjectAccess({
          variables: {
            classroomId: classroomArg.getId(),
            subjectId: subject.getId(),
            input: {restrictFreePractice: restricted}
          }
        });

        const mutationErrors = result.data?.updateClassroomSubjectAccess?.errors;
        if (mutationErrors && mutationErrors.length > 0) {
          throw new Error(
            mutationErrors[0]?.message ?? 'Failed to update classroom subject access'
          );
        }

        addToast({
          message: `Independent practice has been ${restricted ? 'disabled' : 'enabled'} for ${subject.getName()}.`,
          title: 'Success!',
          color: 'positive'
        });
        classroomQuery.invalidateInterest();
      } catch (_error) {
        addToast({
          message:
            'We encountered an error while saving your changes. Please contact us if the problem persists.',
          title: 'Something went wrong',
          color: 'negative'
        });
      }
    },
    [classroomQuery, updateClassroomSubjectAccess]
  );

  const handleOpenAccess = useCallback(
    async (subject: SubjectModelV2) => {
      handleSubjectAccessChange(classroom, subject, false);
    },
    [classroom, handleSubjectAccessChange]
  );

  const handlePartialCreditToggled = useCallback(
    async (classroomId: string, subject: SubjectModelV2) => {
      const partialCreditEnabled =
        subject
          .get('relationshipMeta')
          .get('classroom')
          .get(classroomId)
          .get('partial_credit_enabled') ?? false;

      try {
        const result = await updateClassroomSubjectAccess({
          variables: {
            classroomId,
            subjectId: subject.getId(),
            input: {partialCreditEnabled: !partialCreditEnabled}
          }
        });

        const mutationErrors = result.data?.updateClassroomSubjectAccess?.errors;
        if (mutationErrors && mutationErrors.length > 0) {
          throw new Error(mutationErrors[0]?.message ?? 'Failed to update partial credit setting');
        }

        addToast({
          message: `Partial credit ${
            partialCreditEnabled ? 'disabled' : 'enabled'
          } for ${subject.getName()}.`,
          title: 'Success!',
          color: 'positive'
        });

        classroomQuery.invalidateInterest();
      } catch (_e) {
        addToast({
          message:
            'We encountered an error while saving your changes. Please contact us if the problem persists.',
          title: 'Something went wrong',
          color: 'negative'
        });
      }
    },
    [classroomQuery, updateClassroomSubjectAccess]
  );

  const debouncedHandlePartialCreditToggled = debounce(
    (classroomId: string, subject: SubjectModelV2) =>
      handlePartialCreditToggled(classroomId, subject),
    250
  );

  if (!isReady) {
    return <LoadingIndicator />;
  }

  if (classroom.getSubjects().isEmpty()) {
    /**
     * If the user is masquerading, we take them to the top-level route of the Course Library (they are not allowed to see "Discover" results for another user).
     */
    return (
      <TrackedEmptyState
        title='No class subjects'
        body='No subjects are currently available for students to practice in this class. Visit the library to assign or open student practice.'
      />
    );
  }

  const classroomId = classroom.getId();
  const unrestrictedSubjects = findUnrestrictedSubjects(classroomId);
  const schoolId = teacher.hasSchool() ? teacher.getSchool().getId() : null;
  const isSchoolLicensed = schoolId != null && Boolean(teacher.getLicenseStatusForSchool(schoolId));

  const triggerElement = (
    <div className='teacher-subjects-list__mobile-dropdown mb-x3 rounded-x1 border-border-primary background-primary p-x2 border border-solid'>
      <Text>{selectedMenuItem}</Text>
      <Icon icon='caret-down' />
    </div>
  );

  return (
    <div
      className={classnames('teacher-subjects-list', {
        'teacher-subjects-list--mobile': isMobile
      })}
    >
      {teacher.getMeta().isLicensed() &&
        isSchoolLicensed &&
        (isMobile ? (
          <MobileDropdown fillWrapper trigger={triggerElement}>
            <DropdownItem onClick={() => setSelectedMenuItem('All class subjects')}>
              All class subjects
            </DropdownItem>
            <DropdownItem onClick={() => setSelectedMenuItem('Independent practice')}>
              Independent practice
            </DropdownItem>
          </MobileDropdown>
        ) : (
          <div className='teacher-subjects-list__menu-list-container mr-x7 rounded-x1 border-border-primary background-primary p-x2! border border-solid'>
            <MenuListItem
              text='All class subjects'
              onMenuItemClick={() => setSelectedMenuItem('All class subjects')}
              selected={selectedMenuItem === 'All class subjects'}
            />
            <MenuListItem
              text='Independent practice'
              onMenuItemClick={() => setSelectedMenuItem('Independent practice')}
              selected={selectedMenuItem === 'Independent practice'}
            />
          </div>
        ))}
      {selectedMenuItem === 'Independent practice' && unrestrictedSubjects.size === 0 && (
        <EmptySubjectsList />
      )}
      {selectedMenuItem === 'Independent practice' && unrestrictedSubjects.size > 0 && (
        <SubjectsList
          classroom={classroom}
          handleCloseAccess={handleCloseAccess}
          handleOpenAccess={handleOpenAccess}
          handlePartialCreditToggled={debouncedHandlePartialCreditToggled}
          selectedMenuItem={selectedMenuItem}
          subjects={unrestrictedSubjects}
          teacher={teacher}
        />
      )}
      {selectedMenuItem === 'All class subjects' && (
        <SubjectsList
          classroom={classroom}
          handleCloseAccess={handleCloseAccess}
          handleOpenAccess={handleOpenAccess}
          handlePartialCreditToggled={debouncedHandlePartialCreditToggled}
          selectedMenuItem={selectedMenuItem}
          subjects={classroom.getSubjects()}
          teacher={teacher}
        />
      )}
      {showModal && (
        <RestrictFreePracticeModal
          classroomId={classroomId}
          fromClassroom
          handleClose={() => {
            setShowModal(false);
          }}
          onRestrict={() => {
            handleSubjectAccessChange(classroom, selectedSubject, true);
          }}
          subject={selectedSubject}
        />
      )}
    </div>
  );
};

export default TeacherSubjectsList;
